'use client';

import React, { useState, useRef, useEffect, FormEvent, ChangeEvent } from 'react';
import { useApi } from '@/context/ApiContext';
import { v4 as uuidv4 } from 'uuid';
import { InputMask } from 'primereact/inputmask';
import { useAdobeAnalytics } from '@/hooks/useAdobeAnalytics';
import { AudioPlayer } from './AudioPlayer';
import SliderToggleTake from './SliderToggleTake';
import { MAX_SUBMISSION_LENGTH, MIN_AUDIO_SUBMISSION_LENGTH } from '@/lib/config';
import { fireConversionEvents } from '@/utils/tracking';
import { getImageURL } from '@/utils/cdn';
import type { SiteSettings, SiteControls } from '@/lib/api/types';

// Define US states array
const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 
    'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 
    'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 
    'WV', 'WI', 'WY'
] as const;

type FormStep = 'form' | 'phone' | 'success';
type TabType = 'text' | 'voice';

const SOCIAL_HANDLE_REGEX = /^@?[A-Za-z0-9._-]{2,30}$/;

interface SubmissionFormProps {
    onSubmissionSuccess: (submission: { id: string; reviewed: boolean }) => void;
    siteConfig?: SiteSettings;
    siteControls?: SiteControls;
    onStepChange?: () => void;
    showSlug: string
}

/**
 * Used for device limit rate limits
 */
const getFingerprint = (): string => {
    let fingerprint = localStorage.getItem('fingerprintId');
    if (!fingerprint) {
        fingerprint = uuidv4();
        localStorage.setItem('fingerprintId', fingerprint);
    }
    return fingerprint;
};

/**
 * Check browser support for audio recording
 */
const getSupportedMimeType = (): string | null => {
    const types = [
        "audio/mp4",
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/webm",
    ];
    for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return null;
};

export default function SubmissionForm({ 
    onSubmissionSuccess, 
    showSlug,
    siteConfig, 
    siteControls, 
    onStepChange 
}: SubmissionFormProps) {
    const { trackEvent } = useAdobeAnalytics();
    const apiClient = useApi();

    // Get max recording length from siteConfig, fallback to constant
    // Ensure it's always a number (convert from string if needed)
    const maxRecordingLength = Number(siteConfig?.voiceTakeLengthSeconds) || MAX_SUBMISSION_LENGTH;

    // --- STATE MANAGEMENT ---
    const [isInitialStep, setIsInitialStep] = useState(true);
    const [formStep, setFormStep] = useState<FormStep>('form');
    const [audioDuration, setAudioDuration] = useState(0);
    const [formError, setFormError] = useState('');
    const [activeTab, setActiveTab] = useState<TabType>('voice');
    const [newSubmissionId, setNewSubmissionId] = useState<string | null>(null);
    const [fingerprintId, setFingerprintId] = useState<string | null>(null);

    // Get fingerprint
    useEffect(() => {
        setFingerprintId(getFingerprint());
    }, []);

    useEffect(() => {
        // Only start scrolling form input position after moving past the "form" step for the first time
        if (isInitialStep && formStep === "form") {
            return;
        } else {
            setIsInitialStep(false);
        }

        if (onStepChange) {
            onStepChange();
        }
    }, [formStep, onStepChange, isInitialStep]);

    // --- ADOBE ANALYTICS TRACKING ---
    useEffect(() => {
        if (formStep === 'phone') {
            trackEvent('Submission Funnel', 'Phone Number Prompt');
        }
        if (formStep === 'success') {
            trackEvent('Submission Funnel', 'Submission Success');
        }
    }, [formStep, trackEvent]);

    useEffect(() => {
        // When the user reaches the phone step, fire all conversion events.
        if (formStep === 'phone') {
            fireConversionEvents();
        }
    }, [formStep]);
    
    // Form field states
    const [text, setText] = useState('');
    const [name, setName] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [socialHandle, setSocialHandle] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [confidenceScore, setConfidenceScore] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [socialHandleError, setSocialHandleError] = useState('');
    const [contactInfoError, setContactInfoError] = useState('');

    // Voice recording states
    const [isRecording, setIsRecording] = useState(false);
    const [voiceStatus, setVoiceStatus] = useState(siteConfig?.recordMessageInstructionText || `Click below to record your message`);
    const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const autoStopTimerRef = useRef<NodeJS.Timeout | null>(null);

    // --- HANDLER FUNCTIONS ---

    /**
     * Handles the initial submission of the take (text or voice).
     */
    const handleInitialSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        trackEvent('Submission Form', siteConfig?.submissionFormMainCTACopy || "Send it to Stephen A. Smith");
        setIsLoading(true);

        setFormError('');
        
        // Validate required fields
        if (!name.trim()) {
            setFormError("Please enter your name.");
            setIsLoading(false);
            return;
        }
        
        if (!city.trim()) {
            setFormError("Please enter your city.");
            setIsLoading(false);
            return;
        }
        
        if (!state) {
            setFormError("Please select your state.");
            setIsLoading(false);
            return;
        }
        
        if (activeTab === 'voice') {
            if (!audioBlob) {
                setFormError("Please record a voice take first.");
                setIsLoading(false);
                return;
            }
            if (audioDuration > 0 && audioDuration < MIN_AUDIO_SUBMISSION_LENGTH) {
                setFormError(`Recording too short. Please record at least ${MIN_AUDIO_SUBMISSION_LENGTH} seconds.`);
                setIsLoading(false);
                return;
            }
        }

        try {
            const submissionData: {
                name: string;
                city: string;
                state: string;
                confidenceScore: string;
                fingerprintId: string;
                showSlug: string;
                text?: string;
                audioUrl?: string;
            } = { 
                name, 
                city, 
                state, 
                showSlug,
                confidenceScore, 
                fingerprintId: fingerprintId || '' 
            };
            
            if (activeTab === 'text') {
                submissionData.text = text;
            } else {
                if (!audioBlob) {
                    alert("Please record a voice take first.");
                    setIsLoading(false);
                    return;
                }
                // Convert audio blob to base64
                const reader = new FileReader();
                const base64Audio = await new Promise<string>((resolve, reject) => {
                    reader.onloadend = () => {
                        const result = reader.result as string;
                        resolve(result);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(audioBlob);
                });
                submissionData.audioUrl = base64Audio;
            }

            const response = await apiClient.user.submit(submissionData);
            const submissionId = response.submissionId;

            // On success, save ID and move to the phone prompt step
            setNewSubmissionId(submissionId);
            onSubmissionSuccess({ id: submissionId, reviewed: false });
            setFormStep('phone');

        } catch (error) {
            console.error("Error submitting take:", error);
            alert('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Handles the submission of the optional phone number.
     */
    const handlePhoneSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        trackEvent('Submission Form', siteConfig?.submissionFormPhoneCTACopy || "Submit");
        setIsSubmitted(true);
        setEmailError('');
        setSocialHandleError('');
        setContactInfoError('');

        const trimmedPhone = phone.trim();
        const trimmedEmail = email.trim();
        const trimmedSocialHandle = socialHandle.trim();
        let hasError = false;

        // Check if at least one contact field is provided
        const hasPhone = trimmedPhone.length === 14;
        const hasEmail = trimmedEmail.length > 0;
        const hasSocialHandle = trimmedSocialHandle.length > 0;

        if (!hasPhone && !hasEmail && !hasSocialHandle) {
            setContactInfoError('Please provide at least one contact method.');
            return;
        }

        // Validate phone if provided
        if (trimmedPhone.length > 0 && trimmedPhone.length !== 14) {
            hasError = true;
        }

        // Validate email if provided
        if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            setEmailError('Provided email is invalid. Please try again.');
            hasError = true;
        }

        // Validate social handle if provided
        if (trimmedSocialHandle && !SOCIAL_HANDLE_REGEX.test(trimmedSocialHandle)) {
            setSocialHandleError('Provided social handle is invalid. Use letters, numbers, ".", "_" or "-" (2-30 characters).');
            hasError = true;
        }

        if (hasError) {
            return;
        }

        setIsLoading(true);
        try {
            if (newSubmissionId) {
                await apiClient.user.addPhoneNumber({ 
                    submissionId: newSubmissionId, 
                    phone: hasPhone ? trimmedPhone : undefined,
                    email: hasEmail ? trimmedEmail : undefined,
                    socialHandle: hasSocialHandle ? trimmedSocialHandle : undefined,
                });
            }
            setFormStep('success');
        } catch (error) {
            console.error("Error adding phone number:", error);
            setFormStep('success');
        } finally {
            setIsLoading(false);
        }
    };

    const handleRecordClick = async () => {
        if (isRecording) {
            trackEvent('Submission Form', 'Stop Record');
            if (autoStopTimerRef.current) {
                clearTimeout(autoStopTimerRef.current);
                autoStopTimerRef.current = null;
            }
            mediaRecorderRef.current?.stop();
            setIsRecording(false);
            setVoiceStatus("Recording stopped. Preparing preview...");
        } else {
            trackEvent('Submission Form', 'Record Voice Take');
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                audioChunksRef.current = [];
                
                const supportedMimeType = getSupportedMimeType();
                if (!supportedMimeType) {
                    setVoiceStatus("Audio recording is not supported on this browser.");
                    return;
                }
                
                const options = { mimeType: supportedMimeType };
                const mediaRecorder = new MediaRecorder(stream, options);
                mediaRecorderRef.current = mediaRecorder;

                mediaRecorder.ondataavailable = (event: BlobEvent) => {
                    if (event.data.size > 0) {
                        audioChunksRef.current.push(event.data);
                    }
                };

                mediaRecorder.onstop = () => {
                    const blob = new Blob(audioChunksRef.current, { type: options.mimeType });
                    const url = URL.createObjectURL(blob);
                    setAudioBlob(blob);
                    setAudioUrl(url);

                    const tempAudio = document.createElement('audio');
                    tempAudio.src = url;
                    tempAudio.addEventListener('loadedmetadata', () => {
                        const duration = tempAudio.duration;
                        setAudioDuration(duration);
                        
                        // Update status based on duration
                        if (duration > 0 && duration < MIN_AUDIO_SUBMISSION_LENGTH) {
                            setVoiceStatus(`Recording too short (${MIN_AUDIO_SUBMISSION_LENGTH}s minimum). Please record again.`);
                        } else {
                            setVoiceStatus("Preview your recording below.");
                        }
                    });

                    stream.getTracks().forEach(track => track.stop());
                };

                mediaRecorder.start();
                setIsRecording(true);
                setVoiceStatus("Recording... Click again to stop.");
                setAudioUrl(null);
                setAudioBlob(null);

                // Automatically stop voice recording after max length
                autoStopTimerRef.current = setTimeout(() => {
                    if (mediaRecorderRef.current?.state === "recording") {
                        mediaRecorderRef.current.stop();
                        setIsRecording(false);
                    }
                }, (maxRecordingLength + 0.5) * 1000);

            } catch (err) {
                console.error("Error accessing microphone:", err);
                setVoiceStatus("Microphone access was denied. Please enable it in your browser settings.");
            }
        }
    };

    const handleResetForm = () => {
        trackEvent('Submission Form', 'Submit another take');
        setFormStep('form');
        setName('');
        setText('');
        setPhone('');
        setEmail('');
        setSocialHandle('');
        setCity('');
        setState('');
        setAudioBlob(null);
        setAudioUrl(null);
        setIsLoading(false);
        setVoiceStatus(siteConfig?.recordMessageInstructionText || `Click below to record your message (${maxRecordingLength}s max)`);
        setEmailError('');
        setIsSubmitted(false);
        setSocialHandleError('');
    };

    useEffect(() => {
        if (formStep === 'phone') {
            setIsSubmitted(false);
            setEmailError('');
            setPhone('');
            setEmail('');
            setSocialHandle('');
            setSocialHandleError('');
            setContactInfoError('');
        }
    }, [formStep]);

    // Duration display effect
    useEffect(() => {
        if (!isRecording) {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
            return;
        }

        setElapsedTime(0);
        intervalRef.current = setInterval(() => {
            setElapsedTime(prevTime => prevTime + 1);
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isRecording]);

    // --- RENDER LOGIC ---

    // STEP 1: RENDER THE INITIAL SUBMISSION FORM
    if (formStep === 'form') {
        return (
            <div>
                <div className="mx-auto mb-5 w-[500px] max-w-[90%] text-center text-[1.6rem] font-extrabold text-black lg:max-w-[80%] lg:text-[2rem]">{siteConfig?.formHeader}</div>
                <form onSubmit={handleInitialSubmit} className="mx-auto w-[500px] max-w-[90%] rounded-2xl bg-[#f5f5f5] p-[35px] text-[0.9em] font-normal text-[#1a1a1a] lg:max-w-[80%]">
                    {/* Custom Slider Toggle for Text/Voice */}
                    {siteControls?.acceptTextSubmissions && (
                        <SliderToggleTake activeTab={activeTab} setActiveTab={setActiveTab} />
                    )}

                    {/* Conditional UI for Text or Voice */}
                    {activeTab === 'text' ? (
                        <>
                            <label className="mx-auto mb-0 mt-1 block pb-[10px] text-center text-[1.1rem] font-semibold">{siteConfig?.submissionFormTextPrompt || ""}</label>
                            <textarea
                                placeholder="Enter your take"
                                className="mb-0 w-full rounded-md border border-[#ccc] bg-white p-[14px] pb-0 text-base font-['Helvetica_Now',_Arial,_sans-serif]"
                                value={text}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                                rows={5}
                                maxLength={siteConfig?.submissionFormMaxChars || 200}
                                required
                            />
                            {siteConfig?.submissionFormMaxChars && (
                                <div
                                    className="mb-[15px] text-right text-[0.8em]"
                                    style={{
                                        color: (siteConfig.submissionFormMaxChars - text.length) <= 10 ? '#d9534f' : '#6c757d',
                                    }}
                                >
                                    Characters Remaining: {siteConfig.submissionFormMaxChars - text.length}
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="text-center">
                            <p id="voice-status" className="mb-4 mt-2 text-sm font-medium text-[#495057]">{voiceStatus}</p>
                            {audioUrl && (
                                <AudioPlayer
                                    audioUrl={audioUrl}
                                    apiClient={apiClient}
                                    className="mb-6 w-full"
                                />
                            )}

                            <button
                                type="button"
                                onClick={handleRecordClick}
                                className={`mb-6 w-full cursor-pointer rounded-full border-none px-4 py-3 text-lg font-medium text-white shadow-lg transition-all active:scale-95 ${
                                    isRecording
                                        ? 'bg-[#dc3545] hover:bg-[#c82333] active:bg-[#bd2130]'
                                        : 'bg-[#d9534f] hover:bg-[#c9302c] active:bg-[#bd2130]'
                                }`}
                            >
                                {isRecording
                                    ? `Stop Recording (${String(elapsedTime).padStart(2, '0')}s)`
                                    : audioBlob
                                    ? `Re-record Message (${maxRecordingLength}s max)`
                                    : `Record Message (${maxRecordingLength}s max)`
                                }
                            </button>
                        </div>
                    )}

                    {/* User Info Fields */}
                    <div>
                        <label className="mx-auto mb-0 block pb-[10px] text-center text-[1.1rem] font-semibold">{siteConfig?.submissionFormNamePrompt || ""}</label>
                        <input
                            type="text"
                            placeholder="Name"
                            required
                            value={name}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
                            className="mb-4 w-full rounded-md border border-[#ccc] bg-white p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif]"
                        />
                        <div className="flex gap-[10px]">
                            <div className="min-w-0 flex-1">
                                <input
                                    type="text"
                                    placeholder="City"
                                    value={city}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
                                    className="mb-4 w-full rounded-md border border-[#ccc] bg-white p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif]"
                                    required
                                />
                            </div>
                            <div className="min-w-0 flex-1">
                                <select
                                    className="mb-4 w-full appearance-none rounded-md border border-[#ccc] bg-white p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif] text-[#333]"
                                    style={{
                                        backgroundImage: `url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2210%22%20height%3D%225%22%20viewBox%3D%220%200%2010%205%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M0%200l5%205%205-5z%22%20fill%3D%22%23333%22/%3E%3C/svg%3E")`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundPosition: 'right 12px center',
                                        backgroundSize: '10px 5px'
                                    }}
                                    value={state} 
                                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setState(e.target.value)} 
                                    required
                                >
                                    <option value="" disabled>State</option>
                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {siteControls?.includeConfidenceDropdown && (
                        <div>
                            <label className="mx-auto mb-0 block pb-[10px] text-center text-[1.1rem] font-semibold">{siteConfig?.submitConfidencePrompt || ''}</label>
                            <select
                                className="mb-4 w-full appearance-none rounded-md border border-[#ccc] bg-white bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20width%3D%2210%22%20height%3D%225%22%20viewBox%3D%220%200%2010%205%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cpath%20d%3D%22M0%200l5%205%205-5z%22%20fill%3D%22%23333%22/%3E%3C/svg%3E')] bg-[right_12px_center] bg-[length:10px_5px] bg-no-repeat p-3 text-base font-['Helvetica_Now',_Arial,_sans-serif] text-[#333]"
                                value={confidenceScore}
                                onChange={(e: ChangeEvent<HTMLSelectElement>) => setConfidenceScore(e.target.value)}
                            >
                                <option value="" disabled>-- Hotline --</option>
                                {siteConfig?.confidenceOptions?.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    {formError && (
                        <p className="text-center font-bold text-[#d9534f]">
                            {formError}
                        </p>
                    )}

                    <button type="submit" className="w-full cursor-pointer rounded-[30px] border-none bg-black px-8 py-4 text-base font-normal text-white transition-all hover:bg-[#0072CE] hover:text-white" disabled={isLoading}>
                        {isLoading ? 'Submitting...' : (siteConfig?.submissionFormMainCTACopy || "Send it to Stephen A. Smith")}
                    </button>

                    {siteConfig?.primaryLegalCopy ? (
                        <p 
                            className="mt-4 text-[0.9em] font-normal text-[#1a1a1a]"
                            dangerouslySetInnerHTML={{ __html: siteConfig.primaryLegalCopy }}
                        />
                    ) : (
                        <p className="mt-4 text-[0.9em] font-normal text-[#1a1a1a]">
                            By pressing &apos;Submit&apos;, you agree to the{' '}
                            <a
                                href="https://www.siriusxm.com/ns/GetSerious/GetSerious_UserSubmissionsAndAppearancesTerms.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3B18FF] underline"
                            >
                                User Submission Terms & Release
                            </a>
                            {' '}and{' '}
                            <a
                                href="https://www.siriusxm.com/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[#3B18FF] underline"
                            >
                                Privacy Policy
                            </a>
                            {' '}and you understand that we may review, rate and discuss your take on the air.
                        </p>
                    )}
                </form>
            </div>
        );
    }

    // STEP 2: RENDER THE PHONE PROMPT
    if (formStep === 'phone') {
        return (
            <div className="mx-auto w-[500px] max-w-[90%] rounded-2xl bg-[#f5f5f5] p-[35px] text-center text-[0.9em] font-normal text-[#1a1a1a] lg:max-w-[80%]">
                <p className="m-0 p-0 text-[2em] font-extrabold">{siteConfig?.submissionFormPhoneTitle || "Thanks, we got it!"}</p>
                <p className="m-0 p-0 text-[1.2em] font-semibold leading-[1.2em]">
                    {siteConfig?.submissionFormPhoneHeading || "One noth thing - want a chance to get on the air?"}
                </p>
                <p className="m-0 p-[10px] text-[0.9em] font-normal leading-[1.2em]">
                    {siteConfig?.submissionFormPhonePrompt || "If he is so compelled and you want to potentially get called give us the digits"}
                </p>
                {contactInfoError && (
                    <p className="mb-3 text-sm font-semibold text-red-600">
                        {contactInfoError}
                    </p>
                )}
                <form onSubmit={handlePhoneSubmit} className="flex flex-col gap-3" noValidate>
                    <InputMask
                        id="phone"
                        mask="(999) 999-9999"
                        placeholder="(999) 999-9999"
                        value={phone}
                        onChange={(e) => {
                            setPhone(e.target.value || '');
                            if (contactInfoError) {
                                setContactInfoError('');
                            }
                        }}
                        className={`w-full rounded-md border p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif] transition-colors ${
                            isSubmitted && phone.trim().length > 0 && phone.trim().length !== 14
                                ? 'border-red-500 bg-red-50'
                                : 'border-[#ccc] bg-white'
                        }`}
                    />
                    {isSubmitted && phone.trim().length > 0 && phone.trim().length !== 14 && (
                        <p className="mt-[-8px] text-left text-sm font-medium text-red-600">
                            Please enter a valid 10-digit phone number.
                        </p>
                    )}

                    <input
                        type="text"
                        inputMode="email"
                        autoComplete="email"
                        aria-invalid={!!emailError}
                        placeholder="E-mail"
                        value={email}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setEmail(event.target.value);
                            if (emailError) {
                                setEmailError('');
                            }
                            if (contactInfoError) {
                                setContactInfoError('');
                            }
                        }}
                        className={`w-full rounded-md border p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif] transition-colors ${
                            emailError ? 'border-red-500 bg-red-50' : 'border-[#ccc] bg-white'
                        }`}
                    />
                    {emailError && (
                        <p className="mt-[-8px] text-left text-sm font-medium text-red-600">
                            {emailError}
                        </p>
                    )}

                    <input
                        type="text"
                        placeholder="Social handle"
                        value={socialHandle}
                        aria-invalid={!!socialHandleError}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            setSocialHandle(event.target.value);
                            if (socialHandleError) {
                                setSocialHandleError('');
                            }
                            if (contactInfoError) {
                                setContactInfoError('');
                            }
                        }}
                        className={`mb-1 w-full rounded-md border p-[14px] text-base font-['Helvetica_Now',_Arial,_sans-serif] transition-colors ${
                            socialHandleError ? 'border-red-500 bg-red-50' : 'border-[#ccc] bg-white'
                        }`}
                    />
                    {socialHandleError && (
                        <p className="mt-[-8px] text-left text-sm font-medium text-red-600">
                            {socialHandleError}
                        </p>
                    )}

                    <button
                        type="submit"
                        className="mt-2 w-full cursor-pointer rounded-[30px] border-none bg-[#3B18FF] px-8 py-4 text-base font-normal text-white transition-all hover:bg-[#5A3BFF]"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Saving...' : (siteConfig?.submissionFormPhoneCTACopy || "Submit")}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            trackEvent('Submission Form', 'No thanks');
                            setPhone('');
                            setEmail('');
                            setSocialHandle('');
                            setEmailError('');
                            setSocialHandleError('');
                            setContactInfoError('');
                            setIsSubmitted(false);
                            setFormStep('success');
                        }}
                        className="mt-1 w-full cursor-pointer rounded-[30px] border border-black bg-white px-8 py-3 text-base font-normal text-black transition-all hover:bg-black hover:text-white"
                    >
                        No thanks
                    </button>
                </form>
                {siteConfig?.secondaryLegalCopy ? (
                    <p 
                        className="mt-4 text-[0.9em] font-normal text-[#1a1a1a]"
                        dangerouslySetInnerHTML={{ __html: siteConfig.secondaryLegalCopy }}
                    />
                ) : (
                    <p className="mt-4 text-[0.9em] font-normal text-[#1a1a1a]">
                        By submitting your information, you agree a member of the show may call or contact you about your take, subject to the{' '}
                        <a
                            href="https://www.siriusxm.com/ns/GetSerious/GetSerious_UserSubmissionsAndAppearancesTerms.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#3B18FF] underline"
                        >
                            User Submission Terms & Release
                        </a>
                        {' '}and{' '}
                        <a
                            href="https://www.siriusxm.com/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#3B18FF] underline"
                        >
                            Privacy Policy
                        </a>.
                    </p>
                )}
            </div>
        );
    }

    // STEP 3: RENDER THE FINAL SUCCESS MESSAGE
    const isVideo = siteConfig?.submissionFormPromoImage && 
        /\.(mp4|webm|ogg|mov)$/i.test(siteConfig.submissionFormPromoImage);
    
    return (
        <div className="mx-auto w-[500px] max-w-[90%] rounded-2xl bg-[#f5f5f5] p-[35px] text-center lg:max-w-[80%]">
            <p className="m-0 p-0 text-[2em] font-extrabold text-black">All Set!</p>
            {siteConfig?.submissionFormPromoImage && (
                <>
                    {isVideo ? (
                        <video
                            className="m-0 my-4 max-w-full rounded-lg"
                            src={getImageURL(siteConfig.submissionFormPromoImage)}
                            controls
                            autoPlay
                            style={{ width: 'auto', height: 'auto', maxHeight: '500px' }}
                        />
                    ) : (
                        <img
                            className="m-0 my-4 max-w-full rounded-lg"
                            src={getImageURL(siteConfig.submissionFormPromoImage)}
                            alt="Listen to the show"
                            width={500}
                            height={300}
                            style={{ width: 'auto', height: 'auto' }}
                        />
                    )}
                </>
            )}
            <p className="m-0 p-[10px] text-[0.9em] font-semibold leading-[1.2em] text-black">{siteConfig?.submissionFormListenPromoText || ""}</p>
            <button
                onClick={() => {
                    trackEvent('Submission Form', siteConfig?.submissionFormListenCTACopy || "Listen on the SiriusXM app");
                    window.open(siteConfig?.submissionFormListenCTAURL || 'https://www.siriusxm.com/apps', '_blank');
                }}
                className="w-full cursor-pointer rounded-[30px] border-none bg-[#0000eb] px-8 py-4 text-base font-normal text-white transition-all hover:bg-[#0000cc]"
            >
                {siteConfig?.submissionFormListenCTACopy || "Listen on the SiriusXM app"}
            </button>
            {siteConfig?.submissionFormSecondCTACopy && siteConfig?.submissionFormSecondCTAURL && (
                <button
                    onClick={() => {
                        trackEvent('Submission Form', siteConfig?.submissionFormSecondCTACopy);
                        window.open(siteConfig.submissionFormSecondCTAURL, '_blank');
                    }}
                    className="mt-4 w-full cursor-pointer rounded-[30px] border-2 border-black bg-white px-8 py-3 text-base font-normal text-black transition-all hover:bg-black hover:text-white"
                >
                    {siteConfig.submissionFormSecondCTACopy}
                </button>
            )}
            <button 
                onClick={handleResetForm} 
                className="mt-4 w-full cursor-pointer rounded-[30px] border-2 border-black bg-white px-8 py-3 text-base font-normal text-black transition-all hover:bg-black hover:text-white"
            >
                Submit another message
            </button>
        </div>
    );
}
